<script setup lang="ts">
import type { Component, PropType } from 'vue';
import GaiaText from './GaiaText.vue';
import { gaiaAccent, gaiaMotion, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  /** Explanation under the label. */
  meta: {
    type: String,
    default: undefined,
  },
  /**
   * A glyph in a small tile before the text (an @vicons component). The tile
   * takes the accent when the card is selected, so a row of choices reads at
   * a glance which one is on. Cards without an icon keep the text-only layout.
   */
  icon: {
    type: Object as PropType<Component>,
    default: undefined,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  /** Not pickable — a mode fixed while a save is in flight. */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ select: [] }>();

const border = gaiaNeutral.border;
const borderHover = gaiaNeutral.borderHover;
const ink = gaiaNeutral.ink;
const surface = gaiaNeutral.surface;
const selectedBg = gaiaNeutral.surfaceSoft;
const tileBg = gaiaNeutral.surfaceMuted;
const tileFg = gaiaNeutral.textSubtle;
const tileSelectedBg = gaiaAccent.surface;
const tileSelectedFg = gaiaAccent.base;
const quick = gaiaMotion.quick;
const easeOut = gaiaMotion.easeOut;
</script>

<template>
  <!-- Selectable mode card from the move-file modal (existing contract vs new
       contract). A radio-style card — n-radio can't carry this layout. -->
  <button
    type="button"
    class="gaia-choice-card"
    :class="{ selected: props.selected }"
    :disabled="props.disabled"
    @click="emit('select')"
  >
    <span v-if="props.icon" class="gaia-choice-card__tile" aria-hidden="true">
      <n-icon :size="18"><component :is="props.icon" /></n-icon>
    </span>
    <span class="gaia-choice-card__text">
      <gaia-text size="dense" weight="bold" tone="ink">{{ props.label }}</gaia-text>
      <gaia-text v-if="props.meta" size="meta" tone="subtle" :style="{ lineHeight: 1.45 }">
        {{ props.meta }}
      </gaia-text>
    </span>
  </button>
</template>

<style scoped>
.gaia-choice-card {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 13px;
  border: 1px solid v-bind(border);
  border-radius: 9px;
  background: v-bind(surface);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color v-bind(quick) v-bind(easeOut),
    background-color v-bind(quick) v-bind(easeOut);
}

.gaia-choice-card__text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.gaia-choice-card__tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: v-bind(tileBg);
  color: v-bind(tileFg);
  transition:
    background-color v-bind(quick) v-bind(easeOut),
    color v-bind(quick) v-bind(easeOut);
}

.gaia-choice-card:hover {
  border-color: v-bind(borderHover);
}

.gaia-choice-card.selected {
  border-color: v-bind(ink);
  background: v-bind(selectedBg);
}

.gaia-choice-card.selected .gaia-choice-card__tile {
  background: v-bind(tileSelectedBg);
  color: v-bind(tileSelectedFg);
}

/* Dims the whole card rather than its text, so a chosen-but-locked mode still
   reads as chosen. `disabled` on the button already blocks the click. */
.gaia-choice-card:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (prefers-reduced-motion: reduce) {
  .gaia-choice-card,
  .gaia-choice-card__tile {
    transition: none;
  }
}
</style>
